/**
 * Tab Unidades - Página principal com grid de KPIs, gráficos e mapa
 */
import { useState } from 'react';
import { UnidadesPrintReport, type UnitsPrintSnapshot } from './components/UnidadesPrintReport';
import {
  KpiCard,
  DonutFarolChart,
  DonutCnpjChart,
  BrazilMap,
  NovasBaixadasChart,
  SyncStatusBadge,
  UnidadesFilters,
} from './components';
import {
  useUnitsKpis,
  useUnitsByUF,
  useNovasBaixadas,
  useUnitsWithCnpjKpis,
  useDocsWithCnpjKpis,
  useSyncEvents,
  useManualSync,
  useUnidadesFilters,
  useUnits,
} from './hooks';
import { Building2, FileCheck, Flame, CheckCircle, Clock, AlertTriangle, Download, Loader2, Printer } from 'lucide-react';
import type { BrazilUF } from '@claro/shared';
import * as XLSX from 'xlsx';
import type { Unit } from './hooks/useUnitsData';

const SHOW_UNIDADES_CNPJ_METRICS = false;

export function UnidadesTab() {
  // Hooks de dados
  const { data: kpis, isLoading: loadingKpis, isFetching: fetchingKpis, isError: errorKpis } = useUnitsKpis();
  const { data: unitsByUF, isLoading: loadingUF, isFetching: fetchingUF, isError: errorUF } = useUnitsByUF();
  const { data: novasBaixadas, isLoading: loadingNB, isFetching: fetchingNB, isError: errorNB } = useNovasBaixadas();
  const { data: kpisCnpj, isLoading: loadingCnpj } = useUnitsWithCnpjKpis();
  const { data: kpisDocsCnpj, isLoading: loadingDocsCnpj, isFetching: fetchingDocs, isError: errorDocs } = useDocsWithCnpjKpis();
  const { data: units, isLoading: loadingUnits } = useUnits();

  // Hook de sincronização
  const { status: syncStatus } = useSyncEvents();
  const { triggerSync, instantRefresh, isTriggering, isRefreshing } = useManualSync();

  // Filtros
  const { setUF, uf: selectedUF, regional } = useUnidadesFilters();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [printSnapshot, setPrintSnapshot] = useState<UnitsPrintSnapshot | null>(null);
  const preparingPrint = fetchingKpis || fetchingUF || fetchingNB || fetchingDocs;
  const printUnavailable = preparingPrint || errorKpis || errorUF || errorNB || errorDocs ||
    !kpis || !unitsByUF || !novasBaixadas || !kpisDocsCnpj;

  // Handler para clique no mapa
  const handleUFClick = (uf: BrazilUF | null) => {
    setUF(uf);
  };

  const exportUnits = () => {
    if (!units?.length) {
      alert('Nenhuma unidade foi encontrada com os filtros atuais.');
      return;
    }
    const rows = units.map((unit: Unit) => ({
      Unidade: unit.UnitName || unit.Identification || '—',
      GUID: unit.UnitGuid,
      Identificação: unit.Identification || '—',
      Situação: unit.SituationName || unit.UnitSituationName || '—',
      Status: unit.StatusUnitName || '—',
      Regional: unit.RegionalName || '—',
      'Cidade / UF': unit.CityStateName || '—',
      CNPJ: unit.Cnpj || unit.RegisteredNumber || '—',
      Polo: unit.PoleName || '—',
      'Data de criação': unit.CreatedAt ? new Date(unit.CreatedAt) : null,
      'Data de encerramento': unit.EndDate ? new Date(unit.EndDate) : null,
    }));
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(rows, { cellDates: true });
    sheet['!cols'] = [
      { wch: 34 }, { wch: 22 }, { wch: 38 }, { wch: 20 }, { wch: 20 }, { wch: 16 },
      { wch: 28 }, { wch: 20 }, { wch: 22 }, { wch: 18 }, { wch: 20 },
    ];
    if (sheet['!ref']) {
      const range = XLSX.utils.decode_range(sheet['!ref']);
      for (let column = range.s.c; column <= range.e.c; column++) {
        const header = String(sheet[XLSX.utils.encode_cell({ r: range.s.r, c: column })]?.v ?? '');
        if (!header.toLowerCase().includes('data')) continue;
        for (let row = range.s.r + 1; row <= range.e.r; row++) {
          const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
          if (cell?.t === 'd') cell.z = 'dd/mm/yyyy';
        }
      }
      sheet['!autofilter'] = { ref: sheet['!ref'] };
    }
    XLSX.utils.book_append_sheet(workbook, sheet, 'Unidades');
    XLSX.writeFile(workbook, `unidades-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const printUnitsPage = () => {
    if (printUnavailable) return;
    // Freeze the displayed data: a background sync must not change an open report.
    setPrintSnapshot({ kpis, unitsByUF, novasBaixadas, docs: kpisDocsCnpj,
      regional, uf: selectedUF, selectedYear, generatedAt: new Date().toISOString(), lastSync: syncStatus.lastSync });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      {printSnapshot && <UnidadesPrintReport snapshot={printSnapshot} onClose={() => setPrintSnapshot(null)} />}
      {/* Header com filtros e status */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="print-exclude"><UnidadesFilters /></div>
        <div className="flex flex-wrap items-center gap-2 print-exclude">
          <button type="button" onClick={exportUnits} disabled={loadingUnits} className="inline-flex h-9 items-center gap-2 rounded-md border bg-background px-3 text-sm font-medium transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50" title="Exportar unidades filtradas">
            {loadingUnits ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Exportar unidades
          </button>
          <button type="button" onClick={printUnitsPage} disabled={printUnavailable} className="inline-flex h-9 items-center gap-2 rounded-md border bg-background px-3 text-sm font-medium transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50" title={printUnavailable ? 'Aguarde o carregamento dos indicadores; em caso de erro, atualize a página.' : 'Visualizar relatório em 2 páginas A4'}>
            <Printer className="h-4 w-4" />
            Imprimir A4
          </button>
          <SyncStatusBadge
            status={syncStatus}
            onManualSync={triggerSync}
            onQuickRefresh={instantRefresh}
            isTriggering={isTriggering}
            isRefreshing={isRefreshing}
          />
        </div>
      </div>

      {/* Grid principal - 18 colunas */}
      <div className="grid grid-cols-18 gap-4">
        {/* Linha 1: KPIs (span 6 cada) - altura fixa para alinhar */}
        <div className="col-span-18 lg:col-span-6 h-[140px]">
          <KpiCard
            title="Total Unidades"
            total={kpis?.totalUnidades || 0}
            icon={<Building2 className="w-5 h-5" />}
            isLoading={loadingKpis}
            className="h-full"
          />
        </div>
        <div className="col-span-18 sm:col-span-9 lg:col-span-6 h-[140px]">
          <KpiCard
            title="Alvará de Funcionamento"
            total={kpis?.alvara.regular || 0}
            subtitle="Regulares"
            statusDetail={kpis?.alvara.detail}
            icon={<FileCheck className="w-5 h-5" />}
            isLoading={loadingKpis}
            className="h-full"
          />
        </div>
        <div className="col-span-18 sm:col-span-9 lg:col-span-6 h-[140px]">
          <KpiCard
            title="AVCB - Auto de Vistoria do Corpo de Bombeiros"
            total={kpis?.avcb.regular || 0}
            subtitle="Regulares"
            statusDetail={kpis?.avcb.detail}
            icon={<Flame className="w-5 h-5" />}
            isLoading={loadingKpis}
            className="h-full"
          />
        </div>

        {/* Linha 2: Mapas separados de regularidade */}
        <div className="col-span-18 lg:col-span-9 h-[350px] order-1 lg:order-none">
          <BrazilMap
            data={unitsByUF || []}
            selectedUF={selectedUF}
            onUFClick={handleUFClick}
            regularityType="alvara"
            title="Mapa de Regularidade - Alvarás"
            isLoading={loadingUF}
            className="h-full"
          />
        </div>

        <div className="col-span-18 lg:col-span-9 h-[350px] order-2 lg:order-none">
          <BrazilMap
            data={unitsByUF || []}
            selectedUF={selectedUF}
            onUFClick={handleUFClick}
            regularityType="avcb"
            title="Mapa de Regularidade - AVCBs"
            isLoading={loadingUF}
            className="h-full"
          />
        </div>

        {/* Linha 3: Faróis */}
        <div className="col-span-9 lg:col-span-9 h-[350px] order-3 lg:order-none">
          <DonutFarolChart
            title="Farol Alvará"
            data={{
              regular: kpis?.alvara.regular || 0,
              emAndamento: kpis?.alvara.em_andamento || 0,
              irregular: kpis?.alvara.irregular || 0,
            }}
            isLoading={loadingKpis}
            className="h-full"
          />
        </div>

        <div className="col-span-9 lg:col-span-9 h-[350px] order-4 lg:order-none">
          <DonutFarolChart
            title="Farol AVCB"
            data={{
              regular: kpis?.avcb.regular || 0,
              emAndamento: kpis?.avcb.em_andamento || 0,
              irregular: kpis?.avcb.irregular || 0,
            }}
            isLoading={loadingKpis}
            className="h-full"
          />
        </div>

        {SHOW_UNIDADES_CNPJ_METRICS && (
        <div className="col-span-18 lg:col-span-9 order-4 flex flex-col gap-4">
          {/* Card detalhado de Unidades Regulares com CNPJ */}
          <div className="h-[140px]">
            <KpiCard
              title="Unidades com CNPJ (Ativos)"
              total={kpisCnpj?.regular || 0}
              subtitle="Regulares"
              statusDetail={{
                vigente: kpisCnpj?.regularDetail?.vigente || 0,
                permanente: kpisCnpj?.regularDetail?.permanente || 0,
                isento: kpisCnpj?.regularDetail?.isento || 0,
                vigenteEmRenovacao: kpisCnpj?.regularDetail?.vigenteRenovacao || 0,
              }}
              icon={<CheckCircle className="w-5 h-5 text-emerald-500" />}
              isLoading={loadingCnpj}
              className="h-full"
            />
          </div>
          
          {/* Gráfico Donut CNPJ abaixo do card */}
          <DonutCnpjChart
            title="Distribuição por Status"
            data={kpisCnpj}
            isLoading={loadingCnpj}
            className="h-[350px]"
          />
        </div>
        )}

        {/* Coluna direita: Documentos com CNPJ (span 9 = 50%) */}
        <div className={`col-span-18 ${SHOW_UNIDADES_CNPJ_METRICS ? 'lg:col-span-9' : 'lg:col-span-18'} order-4 flex flex-col gap-4`}>
          {/* Card detalhado de Documentos Regulares com CNPJ */}
          <div className="h-[140px]">
            <KpiCard
              title="Documentos com CNPJ (Ativos)"
              total={kpisDocsCnpj?.regular || 0}
              subtitle="Regulares"
              statusDetail={{
                vigente: kpisDocsCnpj?.regularDetail?.vigente || 0,
                permanente: kpisDocsCnpj?.regularDetail?.permanente || 0,
                isento: kpisDocsCnpj?.regularDetail?.isento || 0,
                vigenteEmRenovacao: kpisDocsCnpj?.regularDetail?.vigenteRenovacao || 0,
              }}
              icon={<FileCheck className="w-5 h-5 text-emerald-500" />}
              isLoading={loadingDocsCnpj}
              className="h-full"
            />
          </div>
          
          {/* Gráfico Donut Documentos CNPJ abaixo do card */}
          <DonutCnpjChart
            title="Distribuição por Status"
            data={kpisDocsCnpj}
            isLoading={loadingDocsCnpj}
            className="h-[350px]"
          />
        </div>

        {/* Linha 4: Gráfico de linha Novas x Baixadas (span completo) */}
        <div className="col-span-18 order-5">
          <NovasBaixadasChart
            data={novasBaixadas || []}
            isLoading={loadingNB}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        </div>
      </div>
    </div>
  );
}
